/* eslint-disable max-lines */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Form, FormSpy } from 'react-final-form'
import RouterPromptStay from 'app/router/RouterPromptStay'
import cx from 'classnames'
import { ValidationErrors } from 'final-form'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'

import useGetBrandVoicesQuery from 'common/api/queries/useGetBrandVoicesQuery'
import { parseFieldsConfiguration } from 'common/components/dynamicFields/helpers'
import {
  ConditionalValue,
  FieldType,
} from 'common/components/dynamicFields/interfaces'
import InfoAction from 'common/components/InfoAction'
import ModalComponent from 'common/components/Modal'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { mapBEMergeTag } from 'features/admin/accountSettings/api/helpers'
import useGenerateVariantsMutation from 'features/unifiedFlow/api/mutations/useGenerateVariantsMutation'
import { userInputFormHasChanged } from 'features/unifiedFlow/store/unifiedFlowSlice'
import { selectTopics } from 'features/unifiedFlow/store/unifiedFlowSlice'

import {
  getAllTemplateElements,
  getIsOptimized,
  getStepStatus,
  validateWorkflowName,
} from '../../helpers'
import { useContent, useSelectedElement, useTopics } from '../../hooks'
import useCheckVariants from '../hooks/useCheckVariants'

import { useBriefGeneration } from './hooks/useBriefGeneration'
import { useDebouncedBriefAnalysis } from './hooks/useDebouncedBriefAnalysis'
import { useWorkflowName } from './hooks/useWorkflowName'
import ChangesOverlay from './ChangesOverlay'
import customerJourneysData from './customerJourneys.json'
import DynamicForm from './DynamicForm'
import FormLeaveModal from './FormLeaveModal'
import GeneratedTopics from './GeneratedTopics'
import { getSubmitAction } from './helpers'
import RegenerateModal from './RegenerateModal'

export type FormSubmitAction = (
  formValues: Record<string, ConditionalValue<FieldType> | undefined>
) => Promise<void>
export type PageState =
  | 'formChangedWithTopics'
  | 'formChangedWithBrief'
  | 'formChangedWithoutTopics'
  | 'brandVoiceChanged'
  | 'mergeTagsChanged'
  | 'topicsChanged'
  | 'generateBrief'
  | 'generateVariants'
  | 'showGeneratedContentWithoutTopics'
  | 'showGeneratedContent'
  | 'generateWithoutTopics'

type Props = {
  isWebsocketLoading: boolean
}

export interface CustomerJourney {
  customerJourneyTitle: string
  description: string
  requirements: string[]
  labels: string[]
}

export const customerJourneys: CustomerJourney[] = customerJourneysData

const BriefAnalysisPage = ({ isWebsocketLoading }: Props) => {
  const dispatch = useAppDispatch()
  const { content } = useContent()
  const { hasVariants } = useCheckVariants()
  const { data: brandVoices } = useGetBrandVoicesQuery()
  const { updateWorkflowName } = useWorkflowName(content)

  const [resetWarningModalState, setResetWarningModalState] = useState<{
    isOpen: boolean
    onConfirm?: () => void
  }>({
    isOpen: false,
    onConfirm: undefined,
  })
  const isModalOpenRef = useRef(false)
  const processedGeneratedTopicsRef = useRef<string[]>([])
  const autoTriggeredAnalysisRef = useRef<string | null>(null)
  const customerJourneyInitializedRef = useRef(false)
  const initialBriefValueRef = useRef<string>('')
  const isInitializedRef = useRef(false)
  const [isFormValid, setIsFormValid] = useState(true)
  const [activeTab, setActiveTab] = useState<'describe' | 'paste'>('describe')
  const [selectedCustomerJourney, setSelectedCustomerJourney] =
    useState<CustomerJourney>(customerJourneys[0])
  const [briefText, setBriefText] = useState('')
  const [isBriefSectionCollapsed, setIsBriefSectionCollapsed] = useState(false)
  const [showCustomerJourneyModal, setShowCustomerJourneyModal] =
    useState(false)

  const selectedTopics = useAppSelector(
    (state) => state.unifiedFlow.selectedTopics
  )

  const allTemplateElements = getAllTemplateElements(content)

  const { data: selectedComponentElement } = useSelectedElement()

  const parentTemplateElement = allTemplateElements.find((templateElement) =>
    templateElement.elements.some(
      (element) => selectedComponentElement?.element_id === element.element_id
    )
  )
  const selectedElement = parentTemplateElement || selectedComponentElement

  const { data: { topics } = {} } = useTopics(
    content?._id,
    selectedElement?.element_id
  )

  const generateVariantsMutation = useGenerateVariantsMutation()

  const isOptimized = getIsOptimized(selectedElement)

  const [hasFormChanged, setHasFormChanged] = useState(false)
  const [hasBrandVoiceChanged, setHasBrandVoiceChanged] = useState(false)
  const [hasMergeTagsChanged, setHasMergeTagsChanged] = useState(false)

  const shouldUseTopics = content?.requires_ner

  const isGlobalSettings = selectedElement === undefined

  const userInputForm = !isGlobalSettings
    ? cloneDeep(
        selectedElement?.user_input?.user_input_form?.filter(
          (field) => field.name !== 'content_name'
        )
      )
    : cloneDeep(content?.user_input.user_input_form)

  useEffect(() => {
    customerJourneyInitializedRef.current = false
  }, [selectedElement?.element_id])

  useEffect(() => {
    if (customerJourneyInitializedRef.current) {
      return
    }

    const customerJourneyField = userInputForm?.find(
      (field) => field.name === 'customer_journey'
    )

    if (
      customerJourneyField?.value &&
      typeof customerJourneyField.value === 'string'
    ) {
      try {
        const parsed = (() => {
          try {
            return JSON.parse(customerJourneyField.value) as {
              journey_title?: string
            }
          } catch {
            return null
          }
        })()

        const journeyTitle = (
          parsed?.journey_title ?? customerJourneyField.value
        ).trim()

        const matchingJourney = customerJourneys.find(
          (journey) =>
            journey.customerJourneyTitle.toLowerCase() ===
            journeyTitle.toLowerCase()
        )

        if (matchingJourney) {
          setSelectedCustomerJourney(matchingJourney)
          customerJourneyInitializedRef.current = true
        }
      } catch (error) {
        customerJourneyInitializedRef.current = true
      }
    } else {
      customerJourneyInitializedRef.current = true
    }
  }, [userInputForm, selectedElement?.element_id])

  const userInputFormForDisplay = userInputForm?.filter(
    (field) => field.name !== 'customer_journey'
  )
  const parsedConfiguration = parseFieldsConfiguration(userInputFormForDisplay)

  const briefField = userInputFormForDisplay?.find(
    (field) =>
      field.name !== 'workflow_name' &&
      field.name !== 'brand_voice' &&
      field.name !== 'merge_tags' &&
      field.name !== 'content_name'
  )

  const generatedBrief = !isGlobalSettings
    ? selectedElement?.user_input?.generated_brief
    : content?.user_input.generated_brief

  const brandVoiceId =
    content?.elements.length === 1
      ? content.elements[0].user_input?.brand_voice_id
      : selectedElement?.user_input?.brand_voice_id
  const elementMergeTags = useMemo(() => {
    return isGlobalSettings
      ? content?.elements[0].user_input?.merge_tags?.map(mapBEMergeTag) || []
      : selectedElement?.user_input?.merge_tags?.map(mapBEMergeTag) || []
  }, [
    isGlobalSettings,
    content?.elements,
    selectedElement?.user_input?.merge_tags,
  ])
  const revertTopics = () => {
    dispatch(selectTopics(topics ?? []))
  }

  const hasTopics = topics !== undefined
  const briefStepsStatus = getStepStatus(content?.steps_status, 'brief')
  const isBriefStepsStatusReadOnly = briefStepsStatus === 'read-only'

  useEffect(() => {
    if (briefField?.value && typeof briefField.value === 'string') {
      setBriefText(briefField.value)
      initialBriefValueRef.current = briefField.value
    } else {
      initialBriefValueRef.current = ''
    }
    // Mark as initialized after the first effect run
    if (!isInitializedRef.current) {
      isInitializedRef.current = true
    }
  }, [briefField?.value])

  // Detect if there's a saved brief and if user has modified it
  // Check both generatedBrief (from backend) and briefField?.value (from form)
  // generatedBrief is more reliable during websocket updates
  const hasSavedBrief = !!generatedBrief || !!briefField?.value
  // Only consider brief changed if we've initialized, briefText is not empty (state has synced),
  // and the values actually differ
  const hasBriefChanged =
    isInitializedRef.current &&
    briefText.trim() !== '' &&
    briefText !== initialBriefValueRef.current

  const {
    analysisResult,
    isLoading: isAnalyzingBrief,
    error: briefAnalysisError,
  } = useDebouncedBriefAnalysis({
    briefText,
    customerJourneyPayload: selectedCustomerJourney,
    contentId: content?._id,
    enabled:
      !isBriefStepsStatusReadOnly &&
      activeTab === 'describe' &&
      (!hasSavedBrief || hasBriefChanged),
  })

  const generationStepsStatus = getStepStatus(
    content?.steps_status,
    'generation'
  )
  const isGenerateStepActive =
    generationStepsStatus === 'enabled' || generationStepsStatus === 'active'

  const getState = (): PageState => {
    const hasTopicsChanged = hasTopics && !isEqual(topics, selectedTopics)

    if (hasBrandVoiceChanged && !hasFormChanged && hasVariants) {
      return 'brandVoiceChanged'
    } else if (hasMergeTagsChanged && !hasFormChanged && hasVariants) {
      return 'mergeTagsChanged'
    } else if (hasFormChanged && !shouldUseTopics && hasVariants) {
      return 'formChangedWithoutTopics'
    } else if (hasFormChanged && shouldUseTopics && hasTopics) {
      return 'formChangedWithTopics'
    } else if (hasFormChanged && shouldUseTopics && generatedBrief) {
      return 'formChangedWithBrief'
    } else if (hasTopicsChanged && hasVariants) {
      return 'topicsChanged'
    } else if (!shouldUseTopics && hasVariants) {
      return 'showGeneratedContentWithoutTopics'
    } else if (!shouldUseTopics && !hasVariants && !hasTopics) {
      return 'generateWithoutTopics'
    } else if (
      !hasVariants &&
      analysisResult &&
      Array.isArray(analysisResult.topics) &&
      analysisResult.topics.length > 0
    ) {
      return 'generateVariants'
    } else if (hasVariants) {
      return 'showGeneratedContent'
    } else {
      return 'generateBrief'
    }
  }

  const state = getState()

  const { generateBrief, generateVariants, isGeneratingBrief } =
    useBriefGeneration({
      content,
      selectedElement,
      isGlobalSettings,
      isOptimized,
      shouldUseTopics,
      userInputForm,
      hasVariants,
      isModalOpenRef,
      setResetWarningModalState,
      selectedCustomerJourney,
    })

  useEffect(() => {
    isModalOpenRef.current = resetWarningModalState.isOpen
  }, [resetWarningModalState.isOpen])

  useEffect(() => {
    if (
      analysisResult?.topics &&
      Array.isArray(analysisResult.topics) &&
      analysisResult.topics.length > 0
    ) {
      const topicsKey = [...analysisResult.topics].sort().join('|')
      if (processedGeneratedTopicsRef.current.includes(topicsKey)) {
        return
      }

      const currentSelectedTopics = selectedTopics || []

      const preservedFocusThemes = currentSelectedTopics.filter(
        (topic) => topic.isConstant === true
      )

      const preservedReplacers = currentSelectedTopics.filter(
        (topic) => topic.type === 'replacer'
      )

      const focusThemeNames = preservedFocusThemes.map((theme) =>
        (theme.name || '').toLowerCase()
      )

      const generatedTopicObjects = analysisResult.topics
        .filter((topicString) => {
          return !focusThemeNames.includes(topicString.toLowerCase())
        })
        .map((topicString, index) => ({
          id: `generated-${topicString}-${Date.now()}-${index}`,
          category: 'generated',
          type: 'topic' as const,
          name: topicString,
          original: {
            nodeId: Date.now() + index,
            node: topicString,
            nodeName: topicString,
            displayName: topicString,
          },
        }))

      processedGeneratedTopicsRef.current.push(topicsKey)
      dispatch(
        selectTopics([
          ...preservedFocusThemes,
          ...preservedReplacers,
          ...generatedTopicObjects,
        ])
      )
    }
  }, [analysisResult?.topics, dispatch, selectedTopics])

  useEffect(() => {
    if (topics && topics.length > 0 && selectedTopics.length === 0) {
      dispatch(selectTopics(topics))
    }
  }, [topics, selectedTopics.length, dispatch])

  useEffect(() => {
    if (
      (state === 'topicsChanged' ||
        state === 'formChangedWithTopics' ||
        state === 'formChangedWithBrief' ||
        state === 'formChangedWithoutTopics') &&
      !isWebsocketLoading
    ) {
      dispatch(userInputFormHasChanged({ hasChanged: true, isGlobalSettings }))
    } else {
      dispatch(userInputFormHasChanged({ hasChanged: false }))
    }
  }, [dispatch, isGlobalSettings, isWebsocketLoading, state])

  const briefQuality = useMemo(() => {
    if (
      analysisResult &&
      analysisResult.brief_analysis?.requirements &&
      analysisResult.brief_analysis.requirements.length > 0
    ) {
      const requirements = analysisResult.brief_analysis.requirements
      const percentage = analysisResult.brief_analysis.overall_score

      const quality =
        percentage >= 90
          ? 'Excellent'
          : percentage >= 75
          ? 'Good'
          : percentage >= 60
          ? 'Fair'
          : 'Poor'

      const criteria = requirements
        .map((req) => {
          const requirementIndex =
            selectedCustomerJourney.requirements.findIndex((journeyReq) => {
              return (
                journeyReq === req.requirement ||
                journeyReq.startsWith(`${req.requirement}:`) ||
                journeyReq.startsWith(`${req.requirement} `)
              )
            })

          if (requirementIndex === -1) {
            return null
          }

          return {
            met: req.fulfilled,
            score: req.score,
            label: selectedCustomerJourney.labels[requirementIndex],
          }
        })
        .filter(
          (
            criterion
          ): criterion is { met: boolean; score: number; label: string } =>
            criterion !== null
        )

      return { criteria, percentage, quality }
    }

    return { criteria: [], percentage: 0, quality: 'Poor' }
  }, [analysisResult, selectedCustomerJourney])

  const formInitialValues: any = useMemo(() => {
    const useInputFormInitialValues = userInputFormForDisplay?.reduce(
      (acc, state) => {
        if (briefField && state.name === briefField.name) {
          return { ...acc, [state.name]: briefText || state.value }
        }
        return { ...acc, [state.name]: state.value }
      },
      {}
    )

    const foundBrandVoice = brandVoices?.find(
      (brandVoice) => brandVoice.id === brandVoiceId
    )
    const brandVoice = foundBrandVoice
      ? { value: foundBrandVoice.id, label: foundBrandVoice.name }
      : undefined

    return {
      ...useInputFormInitialValues,
      brand_voice: brandVoice,
      merge_tags: elementMergeTags?.map((mergeTag) => mergeTag.id) || [],
    }
  }, [
    brandVoiceId,
    brandVoices,
    briefText,
    briefField,
    elementMergeTags,
    userInputFormForDisplay,
  ])

  useEffect(() => {
    if (generateVariantsMutation.isLoading) {
      return
    }

    if (hasVariants) {
      return
    }

    const analysisKey = analysisResult
      ? JSON.stringify(
          Array.isArray(analysisResult.topics)
            ? analysisResult.topics.sort()
            : null
        )
      : null

    const shouldTrigger =
      !isAnalyzingBrief &&
      !briefAnalysisError &&
      analysisResult &&
      briefText.trim() &&
      isFormValid &&
      analysisKey &&
      analysisKey !== autoTriggeredAnalysisRef.current

    if (shouldTrigger) {
      autoTriggeredAnalysisRef.current = analysisKey

      const shouldRefresh = !!generatedBrief
      const formValues = {
        ...formInitialValues,
        ...(briefField && { [briefField.name]: briefText }),
        submitAction: shouldRefresh ? 'refreshBrief' : 'generateBrief',
      }

      generateBrief(formValues)
    }
  }, [
    analysisResult,
    isAnalyzingBrief,
    briefAnalysisError,
    briefText,
    isFormValid,
    generatedBrief,
    formInitialValues,
    briefField,
    generateBrief,
    generateVariantsMutation.isLoading,
    hasVariants,
  ])

  return (
    <div className="flex flex-col flex-1 max-w-180">
      {userInputForm && (
        <Form
          onSubmit={(values) => {
            const valuesWithBrief = {
              ...values,
              ...(briefField && { [briefField.name]: briefText }),
            }
            getSubmitAction(
              valuesWithBrief,
              generateBrief,
              generateVariants
            )(valuesWithBrief)
          }}
          initialValues={formInitialValues}
          key={selectedElement?.element_id}
          validate={(values) => {
            const errors: ValidationErrors = {}
            const workflowNameValidationError = validateWorkflowName(
              values['workflow_name']
            )

            if (!!workflowNameValidationError) {
              errors.workflow_name = workflowNameValidationError
            }

            return errors
          }}
          render={({ handleSubmit, submitting, dirty, submitSucceeded }) => (
            <form
              onSubmit={handleSubmit}
              onBlur={(e) => {
                if (
                  e.target.name === 'workflow_name' &&
                  e.target.value !== ''
                ) {
                  updateWorkflowName(e.target.value)
                }
              }}
            >
              <FormSpy
                onChange={(formState) => {
                  setIsFormValid(!formState.invalid)

                  const dirtyFieldsKeys = Object.keys(formState.dirtyFields)
                  const briefTextChanged =
                    briefText !== (briefField?.value || '')
                  const dirtyFields = dirtyFieldsKeys.filter(
                    (field) =>
                      field !== 'workflow_name' &&
                      field !== 'brand_voice' &&
                      field !== 'merge_tags'
                  )
                  const isDirty = dirtyFields.length > 0 || briefTextChanged
                  setHasFormChanged(isDirty)

                  const brandVoiceChanged =
                    dirtyFieldsKeys.includes('brand_voice') && !isDirty
                  setHasBrandVoiceChanged(brandVoiceChanged)

                  const mergeTagsChanged =
                    dirtyFieldsKeys.includes('merge_tags') && !isDirty
                  setHasMergeTagsChanged(mergeTagsChanged)
                }}
              />

              {parsedConfiguration && (
                <DynamicForm
                  fieldsConfiguration={parsedConfiguration}
                  isDisabled={submitting || isBriefStepsStatusReadOnly}
                  pageState={state}
                  isLoading={generateVariantsMutation.isLoading}
                  brandVoiceId={brandVoiceId}
                  isOptimized={isOptimized}
                  briefField={briefField}
                  briefText={briefText}
                  setBriefText={setBriefText}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  selectedCustomerJourney={selectedCustomerJourney}
                  setShowCustomerJourneyModal={setShowCustomerJourneyModal}
                  isBriefSectionCollapsed={isBriefSectionCollapsed}
                  setIsBriefSectionCollapsed={setIsBriefSectionCollapsed}
                  briefQuality={briefQuality}
                  isAnalyzingBrief={isAnalyzingBrief}
                  briefAnalysisError={briefAnalysisError}
                  analysisResult={analysisResult}
                  hasSavedBrief={hasSavedBrief}
                  hasBriefChanged={hasBriefChanged}
                />
              )}
              <div className="relative">
                {state === 'brandVoiceChanged' && <ChangesOverlay />}
                <GeneratedTopics
                  contentId={content?._id}
                  topics={selectedTopics}
                  pageState={state}
                  setSelectedTopics={(topicItems) =>
                    dispatch(selectTopics(topicItems))
                  }
                  revertTopics={revertTopics}
                  isReadOnly={isBriefStepsStatusReadOnly}
                  isGeneratingVariants={generateVariantsMutation.isLoading}
                  isGenerateStepActive={isGenerateStepActive}
                  isAnalyzingBrief={isAnalyzingBrief}
                  isGeneratingBrief={isGeneratingBrief}
                  briefText={briefText}
                  analysisResult={analysisResult}
                />
              </div>
              <RouterPromptStay
                shouldShow={
                  !!content &&
                  dirty &&
                  !submitting &&
                  !submitSucceeded &&
                  !isBriefStepsStatusReadOnly
                }
                onOK={() => Promise.resolve(true)}
              />
            </form>
          )}
        />
      )}
      <FormLeaveModal />
      <RegenerateModal
        isOpen={resetWarningModalState.isOpen}
        onConfirm={() => {
          resetWarningModalState.onConfirm?.()
          setResetWarningModalState({ isOpen: false })
        }}
        onClose={() => setResetWarningModalState({ isOpen: false })}
      />
      <ModalComponent
        isResponsive
        centered
        visible={showCustomerJourneyModal}
        className="max-w-4xl"
        style={{ maxHeight: '90vh' }}
        closable={true}
        onClose={() => setShowCustomerJourneyModal(false)}
        maskClosable={true}
      >
        <div className="flex flex-col max-h-[90vh]">
          <div className="text-base-700 text-2xl mb-4 flex-shrink-0">
            Customer journey
          </div>
          <div className="text-base-500 mb-6 flex-shrink-0">
            Choose the journey that best fits your campaign's purpose.
          </div>
          <div className="grid grid-cols-3 gap-4 overflow-y-auto flex-1 min-h-0">
            {customerJourneys.map((journey) => {
              const isSelected =
                selectedCustomerJourney?.customerJourneyTitle ===
                journey.customerJourneyTitle
              return (
                <div
                  key={journey.customerJourneyTitle}
                  onClick={() => {
                    setSelectedCustomerJourney(journey)
                    setShowCustomerJourneyModal(false)
                  }}
                  className={cx(
                    'p-4 border border-gold-400 rounded cursor-pointer transition-colors flex items-center justify-between',
                    {
                      'bg-yellow-400': isSelected,
                      'bg-gold-100 hover:bg-gold-40': !isSelected,
                    }
                  )}
                >
                  <span className="font-medium text-base-700">
                    {journey.customerJourneyTitle}
                  </span>
                  <InfoAction
                    message={journey.description}
                    isDefaultColor={false}
                    size={4}
                  />
                </div>
              )
            })}
          </div>
        </div>
      </ModalComponent>
    </div>
  )
}

export default BriefAnalysisPage
